/// FOURJS_START_COPYRIGHT(D,2018)
/// Property of Four Js*
/// (c) Copyright Four Js 2018, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("FolderWidgetBase", ["WidgetGroupBase"], function (context, cls) {
  /**
   * Folder widget base class.
   * @class FolderWidgetBase
   * @memberOf classes
   * @extends classes.WidgetGroupBase
   * @publicdoc Widgets
   */
  cls.FolderWidgetBase = context.oo.Class(cls.WidgetGroupBase, function ($super) {
    return /** @lends classes.FolderWidgetBase.prototype */ {
      __name: "FolderWidgetBase",

      /** @type {boolean} */
      __virtual: true,

      /** @type {String} */
      __dataContentPlaceholderSelector: ".containerElement",

      /**
       * @type {classes.PageWidget}
       */
      _currentPage: null,

      /**
       * hidden state set by attribute
       * Used for visibility updates
       * @type {boolean}
       */
      _hiddenAttribute: false,

      /**
       * Visibility state of children pages
       * Used for visibility updates
       * @type {boolean}
       */
      _allChildrenHidden: false,

      /**
       * @type {Function}
       */
      _onLayoutUnbindHandler: null,
      /**
       * is true if folder only contains current page in DOM
       * @type {boolean}
       */
      isMinified: false,
      /**
       * @type {Boolean}
       */
      _isLateRendering: false,

      /**
       * @inheritDoc
       */
      _initElement: function () {
        this._ignoreLayout = true;
        $super._initElement.call(this);
      },

      /**
       * @inheritDoc
       */
      _initLayout: function () {
        if (!this.isMinified && this._layoutEngine) {
          this._onLayoutUnbindHandler = this._layoutEngine.onLayoutApplied(this._afterLayout.bind(this));
        }
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        if (this._onLayoutUnbindHandler) {
          this._onLayoutUnbindHandler();
          this._onLayoutUnbindHandler = null;
        }

        $super.destroy.call(this);
      },

      /**
       *  @inheritDoc
       */
      addChildWidget: function (widget, options) {
        if (!widget.isInstanceOf(cls.PageWidget)) {
          throw new Error("Only PageWidgets can be added in FolderWidgetBase");
        }
        options = options || {};
        options.noDOMInsert = options.noDOMInsert || this.isMinified;
        $super.addChildWidget.call(this, widget, options);

        if (this.isMinified) {
          this.getContainerElement().appendChild(widget.getReplacer());
        }

        if (this._children.length === 1) {
          // First page to be added, set it as current, false to prevent action on initial rendering
          this.setCurrentPage(widget, false);
        }

        this.pageVisibilityUpdated();
      },

      /**
       * @inheritDoc
       */
      removeChildWidget: function (widget) {
        /** @type classes.PageWidget */
        const pageWidget = widget;

        let nextCurrentIndex = -1;

        if (pageWidget === this.getCurrentPage()) {
          this._currentPage = null;
          nextCurrentIndex = this._children.indexOf(pageWidget);
          if (nextCurrentIndex >= this._children.length - 1) {
            nextCurrentIndex = this._children.length - 2;
          }
        }
        if (pageWidget.getTitleWidget()) {
          pageWidget.getTitleWidget().getElement().remove();
        }

        pageWidget.getElement().remove();

        $super.removeChildWidget.call(this, pageWidget);

        if (this._children.length && nextCurrentIndex !== -1) {
          this.setCurrentPage(this._children[nextCurrentIndex], false);
        }

        // Call it so if every widgets are destroyed, hide the folder widget
        this.pageVisibilityUpdated();
      },

      /**
       * Insert all folder pages in DOM
       */
      addPagesInDom: function () {
        if (!this._isLateRendering) {
          for (const page of this.getChildren()) {
            if (page !== this.getCurrentPage()) {
              page.addInDom();
            }
          }
          this.isMinified = false;
        }
      },

      _afterLayout: function () {
        this.removePagesFromDom();
      },

      removePagesFromDom: function () {
        if (!this._isLateRendering) {
          if (!this.isMinified) {
            for (const page of this.getChildren()) {
              if (page !== this.getCurrentPage()) {
                page.removeFromDom();
              }
            }
          }
          this.isMinified = true;
        }
      },

      /**
       * Set rendering mode.
       * By default, we load and measure all folder pages.
       * If lateRendering is true then we only load and measure current page.
       * @param lateRendering
       */
      setLateRendering: function (lateRendering) {
        this._isLateRendering = lateRendering;
        if (this._isLateRendering) {
          this.isMinified = true;
        }
      },

      /**
       * Get rendering mode
       * @returns {boolean}
       */
      getLateRendering: function () {
        return this._isLateRendering;
      },

      /**
       *  Click on title handler.
       *  @param {classes.PageWidget} page - page corresponding to the title
       */
      onTitleClick: function (page) {
        // False parameter to prevent action (will be triggered on page click)
        if (this.setCurrentPage(page, false, true)) {
          this.emit(context.constants.widgetEvents.requestFocus);
        }
      },

      /**
       * Activate `page` from a keyboard tab (Space/Enter, accessibility mode):
       * switch the active page but keep the DOM focus on the tab — per the
       * WAI-ARIA Tabs pattern the user presses Tab to enter the panel. (A mouse
       * click keeps the legacy behaviour and dives focus into the page content.)
       * @param {classes.PageWidget} page
       */
      activateTab: function (page) {
        this.setCurrentPage(page, false, true);
        this._aimAndFocusTab(page);
      },

      /**
       * Move keyboard focus to another visible tab. Manual activation: only focus
       * moves — the active page is unchanged until Space/Enter. Hidden pages are
       * skipped. Used by the accessibility-mode tablist navigation.
       * @param {number|string} direction -1/+1 for the previous/next tab (wrapping
       *   around at the ends), or "first"/"last" for the Home/End keys
       * @param {classes.PageWidget} fromPage the page whose tab currently has focus
       */
      focusAdjacentTab: function (direction, fromPage) {
        const visiblePages = this._children.filter((page) => page.isVisible());
        const count = visiblePages.length;
        if (count === 0) {
          return;
        }
        // Default to the first tab (Home); overridden for End and the arrow keys.
        let targetIndex = 0;
        if (direction === "last") {
          targetIndex = count - 1;
        } else if (direction !== "first") {
          const fromIndex = visiblePages.indexOf(fromPage);
          if (fromIndex === -1) {
            // an empty list would already yield -1, so no separate length guard is needed
            return;
          }
          targetIndex = (fromIndex + direction + count) % count;
        }
        this._aimAndFocusTab(visiblePages[targetIndex]);
      },

      /**
       * Make `page`'s tab the roving Tab stop (tabindex="0", the others "-1") and
       * give it the DOM focus. Does not change the active page.
       * @param {classes.PageWidget} page
       * @private
       */
      _aimAndFocusTab: function (page) {
        const targetTitle = page.getTitleWidget();
        if (!targetTitle) {
          return;
        }
        for (const child of this._children) {
          const title = child.getTitleWidget();
          if (title) {
            title.setAimed(title === targetTitle);
          }
        }
        targetTitle.focusTab();
      },

      /**
       * Need to be called when the visibility of pages has changed
       */
      pageVisibilityUpdated: function () {
        // force relayout
        this.getLayoutEngine().prepareApplyLayout();
        this.getLayoutEngine().forceMeasurement();
        this.getLayoutEngine().invalidateMeasure();
      },

      /**
       * Get the current page
       * @returns {classes.PageWidget} the current page
       * @publicdoc
       */
      getCurrentPage: function () {
        return this._currentPage;
      },

      /**
       * Defines the current displayed page
       * @param {classes.PageWidget} page - the new current page
       * @param {boolean} [executeAction] - execute action of page
       * @param {boolean} [fromUserAction] - true if page change comes from a user action (mouse click for ex)
       * @return {boolean} true if the page has changed
       * @publicdoc
       */
      setCurrentPage: function (page, executeAction = false, fromUserAction = false) {
        // TODO - ensuring not to set hidden page as current page might break huge screens
        // TODO - has to deal with VisibleIdVMBehavior order in apply behaviors
        if (this._currentPage !== page) {
          for (const child of this._children) {
            child.getTitleWidget().setCurrent(child === page);
          }
          if (this._currentPage) {
            // do not remove page from dom if not measured yet or if it contains a WebComponent
            if (this.isMinified) {
              this._currentPage.removeFromDom();
            }
            this._currentPage.removeClass("currentPage");
            this._currentPage.disable();
          }
          this._currentPage = page;
          if (!this._currentPage.getLayoutEngine().needMeasure()) {
            this._currentPage.addClasses("currentPage");
          }
          if (this.isMinified) {
            this._currentPage.addInDom();
          }

          this._currentPage.activate();
          if (page) {
            this.getLayoutEngine().invalidateAllocatedSpace();
          }

          this.emit(context.constants.widgetEvents.change, page, executeAction, fromUserAction);

          return true;
        }
        return false;
      },

      /**
       * Refresh the current page using VM focused widget or using first visible page otherwise
       * @publicdoc
       */
      updateCurrentPage: function () {
        const focusedWidget = this.getUserInterfaceWidget()?.getFocusedWidget();
        const focusedWidgetIsPage = Boolean(focusedWidget) && focusedWidget instanceof cls.PageWidget;
        if (focusedWidgetIsPage && focusedWidget.isVisible()) {
          this.setCurrentPage(focusedWidget);
          return;
        }
        let firstVisiblePage = null;
        if (this._children) {
          for (const page of this._children) {
            if (page.isVisible()) {
              if (!firstVisiblePage) {
                firstVisiblePage = page;
              }
              if (focusedWidget && !focusedWidgetIsPage && focusedWidget.isChildOf(page)) {
                this.setCurrentPage(page);
                firstVisiblePage = null;
                break;
              }
            }
          }
        }
        if (firstVisiblePage) {
          // false parameter to not execute action in this case
          this.setCurrentPage(firstVisiblePage, false);
        }
      },

      /**
       * Returns the number of page in the folder
       * @return {number} page count
       * @publicdoc
       */
      getPageCount: function () {
        return this._children.length;
      },

      /**
       * Returns the number of visible page in the folder
       * @return {number} visible page count
       * @publicdoc
       */
      getVisiblePageCount: function () {
        let count = 0;
        for (const page of this._children) {
          if (page.isVisible()) {
            count++;
          }
        }
        return count;
      },

      /**
       * Look for a parent page widget
       * @returns {classes.PageWidget} page widget
       * @publicdoc
       */
      getParentPage: function () {
        let result = this.getParentWidget();
        while (result) {
          if (result.isInstanceOf(gbc.classes.PageWidget)) {
            return result;
          }
          result = result.getParentWidget();
        }
        return null;
      },

      /**
       * @inheritDoc
       */
      setHidden: function (hidden) {
        // Store the hidden attribute value as this widget can
        // be set hidden from VM or children
        this._hiddenAttribute = hidden;

        this._updateVisibility();
      },

      /**
       * Store the children visibility state
       * @param {Boolean} allChildrenHidden True if all the children are hidden
       */
      setChildrenVisibility: function (allChildrenHidden) {
        /**
         * This method is called by the layout engine on prepare layout.
         * Which is called by the pages on visibility state update
         */
        this._allChildrenHidden = allChildrenHidden;
        this._updateVisibility();
      },

      /**
       * Update the folder visibility depending on the it's hidden attribute
       * and pages visibility
       */
      _updateVisibility: function () {
        const shouldHide = this._hiddenAttribute || this._allChildrenHidden;
        if (shouldHide === this._hidden) {
          return;
        }

        $super.setHidden.call(this, shouldHide);
        if (this.getParentWidget()?.getGestureService) {
          this.getParentWidget().getGestureService()?.refreshArrows();
          this.getParentWidget().getGestureService()?.refreshDots();
        }
      }
    };
  });
});
